package uz.mint.frontend.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import uz.mint.frontend.EyebrowStyle
import uz.mint.frontend.api.Destination
import uz.mint.frontend.api.flag
import uz.mint.frontend.api.priceLabel
import uz.mint.frontend.gridTemplateColumnsCss
import uz.mint.frontend.hairlineCell
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.SiteStrings
import uz.mint.frontend.toSitePalette

/**
 * The destination picker: where are you going, before what package do you want.
 *
 * Shares [PlanGrid]'s construction — a 2px rule-coloured background showing through 2px gaps, so
 * the cards are separated by hairlines rather than by borders that would double up between
 * neighbours. Deliberately the same visual language, because these cards are the step *before* the
 * plan cards and a customer walks straight from one grid into the other.
 *
 * Cards are narrower than plan cards (188px against 262px): a country card carries a flag, a name
 * and a price, where a plan card also carries the data/validity tag and a full plan name. More
 * countries per row is the whole point of this screen.
 */
@Composable
fun DestinationGrid(
    destinations: List<Destination>,
    locale: Locale,
    strings: SiteStrings,
    href: (Destination) -> String,
    dimmed: Boolean = false,
    onSelect: ((Destination) -> Unit)? = null,
) {
    val palette = ColorMode.current.toSitePalette()

    Div(
        Modifier
            .fillMaxWidth()
            // Filled like a card, not like a rule: the last row is short whenever the count is not
            // a multiple of the column count, and this is the colour those missing cells show.
            .backgroundColor(palette.card)
            .padding(2.px)
            .display(DisplayStyle.Grid)
            .gap(2.px)
            .gridTemplateColumnsCss("repeat(auto-fill,minmax(min(100%,188px),1fr))")
            .opacity(if (dimmed) 0.55 else 1.0)
            .transition(Transition.of("opacity", 160.ms))
            .toAttrs()
    ) {
        destinations.forEach { DestinationCard(it, locale, strings, href(it), onSelect) }
    }
}

/**
 * One destination, as a real `<a href>`.
 *
 * An anchor rather than a button, and that is the whole reason this grid takes an [href] builder.
 * Every destination has an address — `/plans?country=TR` — so the landing page's short list is
 * sixteen crawlable links into a filtered catalogue rather than sixteen dead ends, and any card
 * can be opened in a new tab, bookmarked or shared.
 *
 * [onSelect] then intercepts the plain left click so the full listing still filters in place
 * without a page load. Ctrl/cmd/shift-click and middle click fall through to the browser, which is
 * exactly what the href is there to preserve. The pager on that page works the same way and for
 * the same reasons.
 */
@Composable
private fun DestinationCard(
    destination: Destination,
    locale: Locale,
    strings: SiteStrings,
    href: String,
    onSelect: ((Destination) -> Unit)?,
) {
    val palette = ColorMode.current.toSitePalette()

    A(
        href = href,
        attrs = Modifier
            .fillMaxWidth()
            .backgroundColor(palette.card)
            .hairlineCell(palette.rule)
            .color(palette.ink)
            .textAlign(TextAlign.Start)
            .textDecorationLine(TextDecorationLine.None)
            .padding(top = 18.px, leftRight = 18.px, bottom = 16.px)
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .gap(12.px)
            .minHeight(148.px)
            .cursor(Cursor.Pointer)
            .toAttrs {
                attr("aria-label", destination.name)
                onSelect?.let { select ->
                    onClick { event ->
                        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
                            event.preventDefault()
                            select(destination)
                        }
                    }
                }
            }
    ) {
        Row(Modifier.fillMaxWidth().gap(10.px), verticalAlignment = Alignment.CenterVertically) {
            SpanText(destination.flag(), Modifier.fontSize(28.px).lineHeight(1))
            Spacer()
            SpanText(
                strings.destPlanCount(locale, destination.planCount),
                EyebrowStyle.toModifier().fontSize(10.px).letterSpacing(0.08.em)
                    .color(palette.accentText),
            )
        }

        Column(Modifier.fillMaxWidth().gap(2.px).styleModifier { property("margin-top", "auto") }) {
            // Region names run long ("China mainland & Japan & South Korea") and must wrap inside
            // the card rather than widen it — the grid track is fixed, so an unwrapped name would
            // overflow into its neighbour.
            SpanText(
                destination.name,
                Modifier.fontSize(16.px).fontWeight(FontWeight.Bold)
                    .letterSpacing((-0.015).em).lineHeight(1.25),
            )
        }

        Row(
            Modifier.fillMaxWidth().gap(8.px)
                .borderTop(2.px, LineStyle.Solid, palette.rule).padding(top = 10.px),
            verticalAlignment = Alignment.Bottom,
        ) {
            Column(Modifier.minWidth(0.px)) {
                SpanText(
                    strings.tariffsFrom,
                    EyebrowStyle.toModifier().fontSize(10.px).letterSpacing(0.08.em).opacity(0.55),
                )
                SpanText(
                    destination.priceLabel(strings),
                    Modifier.fontSize(17.px).fontWeight(FontWeight.ExtraBold)
                        .letterSpacing((-0.03).em).lineHeight(1.15),
                )
            }
            Spacer()
            ArrowRightIcon(18, palette.ink)
        }
    }
}
